// TODO: consider move to sparky-one-nav micro-app

import { __console, __isLocalhost } from '../init/debug'
import {
  ADEM_X_APP_ID,
  AIOPS_APP_ID,
  CN_REGION,
  FAWKES_APP_ID,
  GOV_REGION,
  HOME_PATH,
  MIGRATING,
  PAE_APP_ID,
  PA_DEFAULT_ROUTE,
  PA_PANORAMA_APP_ID,
  RUNNING,
  SASE_CLIENT_ID,
  SASE_CN_CLIENT_ID,
  SASE_CN_URL_KEY,
  SASE_URL_KEY,
  SCM_APP_ID,
  SCM_CLIENT_ID,
  SCM_MERGE_APP_IDS,
  SCM_URL_KEY,
  SDWAN_APP_ID,
  SDWAN_PATH_PREFIX,
} from './constants'
import { RedirectionViews } from './enums'
import { getFrameworkServiceUrl, getFrameworkVar, isAppCanBeLaunched } from './states'
import type { Instance, ReadonlyTsgInstances, ReadonlyTsgTree } from './indexedArrays'
import type { UrlOverriding } from './stateTypes'

export const isTsgGovSase = ({ tsg_id, tsgs, instances }: { tsg_id?: string, tsgs?: ReadonlyTsgTree, instances?: ReadonlyTsgInstances }): boolean => {
  const redirectGovToSCM = getFrameworkVar('redirect_fedmod_to_scm') || new Set(getFrameworkVar('fedmod_tsgs_in_scm')).has(tsg_id)
  return redirectGovToSCM ? tsgs?.get(tsg_id)?.isSASE : tsgs?.get(tsg_id)?.isSASE || instances?.platform_region === GOV_REGION
}

const isPaCnL2 = (instance: Instance | undefined): boolean => {
  if (!instance?.entitlements?.length) {
    return false
  }
  const { entitlements, region, platform_region = region } = instance
  return platform_region === CN_REGION &&
    entitlements?.some((ent: { license_type?: string }) => ent.license_type === 'PAN-CHINA-LEVEL2')
}

export const getOverrideTsgView = (tsgId: string | undefined): RedirectionViews | undefined => {
  const overrideTsgView = tsgId && getFrameworkVar<Record<string, RedirectionViews>>('override_redirection_tsgs')?.[tsgId]
  if (!overrideTsgView) {
    return undefined
  }
  if (overrideTsgView && !(Object.values(RedirectionViews).includes(overrideTsgView as RedirectionViews))) {
    __console.error('Invalid override redirection found: ', overrideTsgView)
    return undefined
  }
  __console.info(`Override view/redirection for TSG ${tsgId} is set to ${overrideTsgView}`)
  return overrideTsgView
}

type OverrideMeta = {
  expectedClientId?: string
  overrideTsgView?: RedirectionViews
}
const overrideTsgInstance = (instance: Instance, tsg_id: string, instances: Instance[], { expectedClientId }: OverrideMeta): Instance => {
  let url: URL | undefined
  const ftOverridePaAiops = getFrameworkVar('aiops_free') ? '' : String(getFrameworkVar('override_pa_with_aiops') || '')
  const ftOverridePaOneapp = getFrameworkVar('override_pa_for_oneapp') && !getFrameworkVar('aiops_free')
  const ftRedirectGovToSCM = getFrameworkVar('redirect_fedmod_to_scm') || new Set(getFrameworkVar('fedmod_tsgs_in_scm')).has(tsg_id)
  const oneappUrl = getFrameworkServiceUrl(SCM_URL_KEY)
  const sasePortalUrl = getFrameworkServiceUrl(SASE_URL_KEY)
  const shouldOverrideAiopsOneapp = (aiopsInst?: Instance) => Boolean(!getFrameworkVar('aiops_free') && oneappUrl && aiopsInst &&
    (aiopsInst?.runtime_attributes?.aiops_tsg_premium_greenfield_20230515 || aiopsInst?.url?.startsWith(oneappUrl)))

  const getAppOrigin = (app_id: string) => (isAppCanBeLaunched(app_id) ? window.location.origin : sasePortalUrl)
  const isHiddenFawkes = (instance: Instance, instances?: Instance[]) => {
    if (instance.runtime_attributes?.hidden || instance.extra?.hidden === true) { // check true to avoid affected by scm-merge
      return true
    }
    // temp logic for hide fawkes before provision is done and runtime attributes is returned
    if (!instances?.length || !getFrameworkVar('auto_hide_pa_with_aiops')) {
      return false
    }
    const appIds = new Set(instances.map(inst => inst.app_id))
    return (appIds.has(AIOPS_APP_ID) || appIds.has(SCM_APP_ID)) && !appIds.has(PAE_APP_ID)
  }
  const getFawkesUrl = (instance: Instance) => {
    if (instance.url && (instance.platform_region || instance.region) === CN_REGION) {
      const isScmCn = isPaCnL2(instances.find(inst => inst.app_id === PAE_APP_ID))
      return new URL('/insights', isScmCn ? oneappUrl : instance.url)
    }
    let origin = getAppOrigin(instance.app_id)
    if (instance.url && (instance.platform_region || instance.region) === GOV_REGION) {
      if (!ftRedirectGovToSCM) {
        origin = sasePortalUrl
      }
      else {
        origin = oneappUrl
      }
    }
    if (isHiddenFawkes(instance, instances)) {
      if (!instance.extra?.hidden) {
        const extra = instance.extra || (instance.extra = {})
        extra.hidden = true // temp add hidden before fawkes return it
      }
      delete instance.url // remove url
      origin = ''
    }
    if (ftOverridePaAiops && sasePortalUrl) {
      origin = sasePortalUrl
    }
    if (origin) {
      const fawkesDefaultRoute = getFrameworkVar('apps_can_launch')?.[FAWKES_APP_ID]?.default_route || PA_DEFAULT_ROUTE
      return new URL(fawkesDefaultRoute, origin)
    }
  }
  const urlOverrideCfg = getFrameworkVar<UrlOverriding>('url_overriding')
  if (instance.app_id === FAWKES_APP_ID) {
    const aiopsInst = oneappUrl && ftOverridePaAiops ?
      instances?.find(inst => inst.app_id === AIOPS_APP_ID) : undefined
    if (expectedClientId) {
      // Check override_redirection_tsgs variable value and set the URL accordingly
      // If the value is either 'scm' or 'hybrid' -> it should go to scm
      // If the value is 'sase' then keep it on SASE Portal.
      const netsec_client_id = getFrameworkVar('netsec_client_id') || SCM_CLIENT_ID
      const baseUrl = {
        [SASE_CLIENT_ID]: sasePortalUrl,
        [SCM_CLIENT_ID]: oneappUrl,
        [netsec_client_id]: oneappUrl,
        [SASE_CN_CLIENT_ID]: getFrameworkServiceUrl(SASE_CN_URL_KEY),
      }[expectedClientId] || oneappUrl
      url = new URL(PA_DEFAULT_ROUTE, baseUrl)
    }
    else if (shouldOverrideAiopsOneapp(aiopsInst)) {
      url = new URL(PA_DEFAULT_ROUTE, oneappUrl)
      Object.defineProperties(aiopsInst, {
        app_launch_display_override: { value: SCM_APP_ID, writable: true },
      })
      // const scmFreeInst = instances.find(inst => inst.app_id === AIOPS_FREE_APP_ID)
      // if (scmFreeInst) {
      //   Object.defineProperties(scmFreeInst, {
      //     app_launch_display_override: { value: SCM_FREE_APP_ID, writable: true },
      //   })
      // }
    }
    else if (oneappUrl && ftOverridePaOneapp && instance.runtime_attributes?.is_oneapp_ready &&
      !(ftRedirectGovToSCM && (instance.platform_region || instance.region) === GOV_REGION)) {
      url = new URL(PA_DEFAULT_ROUTE, oneappUrl)
      if (isHiddenFawkes(instance, instances)) {
        if (!instance.extra?.hidden) {
          const extra = instance.extra || (instance.extra = {})
          extra.hidden = true // temp add hidden before fawkes return it
        }
      }
    }
    else {
      const overrideUrl = getFawkesUrl(instance)
      if (overrideUrl) {
        url = overrideUrl
      }
    }
    if (instance.status === RUNNING && instance.extra?.hidden &&
      instance.extra?.migrate_from && instances.some(inst => inst.app_id === PA_PANORAMA_APP_ID)) {
      instance.status = MIGRATING // override status for hidden migrating fawkes
    }
  }
  else if (instance.app_id.startsWith(FAWKES_APP_ID) &&
    (instance.url || instance.app_id === PA_PANORAMA_APP_ID)) {
    const fawkesInst = instances?.find(inst => inst.app_id === FAWKES_APP_ID)
    const overrideUrl = fawkesInst && getFawkesUrl(fawkesInst)
    if (overrideUrl) { // override url
      url = overrideUrl
      if (fawkesInst.client_id) {
        instance.client_id = fawkesInst.client_id
      }
    }
  }
  else if (urlOverrideCfg?.[instance.app_id] && instance.iam_controlled) {
    const { base_url, default_route } = urlOverrideCfg[instance.app_id]
    const baseUrl = base_url.startsWith('https:') ? base_url : getFrameworkServiceUrl(base_url)
    url = new URL(default_route, baseUrl)
  }
  else if (instance.app_id === SDWAN_APP_ID) {
    let origin = getAppOrigin(instance.app_id)
    if (getFrameworkVar('override_sdwan_for_oneapp') && !__isLocalhost) {
      const fawkesInst = instances?.find(inst => inst.app_id === FAWKES_APP_ID)
      const checkInst = fawkesInst?.runtime_attributes ? fawkesInst : instance
      origin = checkInst.runtime_attributes?.is_oneapp_ready ? oneappUrl : sasePortalUrl
    }
    if (origin) {
      url = new URL(SDWAN_PATH_PREFIX, origin)
    }
  }
  else if (instance.app_id === ADEM_X_APP_ID) {
    if (instances?.some(inst => inst.app_id === SCM_APP_ID)) {
      const extra = instance.extra || (instance.extra = {})
      extra.hidden = true
    }
  }
  else if (instance.url?.includes('instance=')) {
    url = new URL(instance.url)
  }
  if (url) {
    url.searchParams.set('tsg_id', tsg_id)
    const strUrl = url.toString()
    if (instance.url && strUrl !== instance.url) {
      instance._org_url = instance.url
    }
    instance.url = strUrl
  }
  return instance
}

export const overrideTsgInstances = (instances: Instance[], tsg_id: string): Instance[] => {
  if (Array.isArray(instances) && instances.length) {
    const overrideTsgView = getOverrideTsgView(tsg_id)
    const netsec_client_id = getFrameworkVar('netsec_client_id') || SCM_CLIENT_ID
    const expectedClientId = overrideTsgView && {
      [RedirectionViews.DEFAULT]: undefined,
      [RedirectionViews.SASE]: SASE_CLIENT_ID,
      [RedirectionViews.SCM]: netsec_client_id,
      [RedirectionViews.HYBRID]: netsec_client_id,
      [RedirectionViews.SCM_CN]: netsec_client_id,
      [RedirectionViews.SASE_CN]: SASE_CN_CLIENT_ID,
    }[overrideTsgView]
    const meta = { overrideTsgView, expectedClientId }
    const overridden = instances.map(inst => {
      try {
        return overrideTsgInstance(inst, tsg_id, instances, meta)
      }
      catch (e) {
        console.error('Error overriding TSG instance:', e, inst)
        return inst
      }
    })
    const fawkesInst = overridden.find(inst => inst.app_id === FAWKES_APP_ID)
    const flags = {} as Record<string, boolean | undefined>
    const isGovRegion = instances.some(inst =>
      (inst.platform_region || inst.region) === GOV_REGION
    )
    const isSCM = getFrameworkVar('redirect_fedmod_to_scm') ||
      new Set(getFrameworkVar('fedmod_tsgs_in_scm')).has(tsg_id) ? true : !isGovRegion

    if (overrideTsgView) {
      flags.is_oneapp_ready = expectedClientId === netsec_client_id
    }
    else if (getFrameworkVar('redirect_sase_to_scm')) {
      flags.is_oneapp_ready = isSCM
    }
    else if (getFrameworkVar('override_pa_for_oneapp') ||
      getFrameworkVar('override_sdwan_for_oneapp') ||
      getFrameworkVar('override_casb_for_oneapp')) {
      flags.is_oneapp_ready = (fawkesInst?.runtime_attributes?.is_oneapp_ready ?? overridden.some(inst =>
        inst.runtime_attributes?.is_oneapp_ready
      )) || (getFrameworkVar('merge_oneapp_tiles') &&
        !SCM_MERGE_APP_IDS.some(appId => overridden.some(inst => inst.app_id === appId)))
    }
    Object.assign(overridden, { flags })

    if (getFrameworkVar('merge_oneapp_tiles') && isSCM && (overridden as any)?.flags?.is_oneapp_ready) {
      // SDWAN will follow PA's flag if in the same TSG, otherwise SDWAN itself, it use own flag
      // NG CASB follows PA
      const oneappAppInsts = SCM_MERGE_APP_IDS.map(
        (appId) => overridden.find(inst => inst.app_id === appId)
      ).filter(Boolean) as Instance[]
      const renameInst = oneappAppInsts.find(inst => inst.status === RUNNING && inst.url) || oneappAppInsts[0]
      const otherInsts = oneappAppInsts.filter(inst => inst !== renameInst)
      const scmBaseUrl = getFrameworkServiceUrl(SCM_URL_KEY)
      if (renameInst && !renameInst.app_launch_display_override && scmBaseUrl) {
        const scmPathOverride = getFrameworkVar('url_overriding')?.[SCM_APP_ID]
        if (scmPathOverride) {
          const baseUrl = scmPathOverride.base_url?.startsWith('https:') ? scmPathOverride.base_url :
            getFrameworkServiceUrl(scmPathOverride.base_url) || scmBaseUrl
          const urlObj = new URL(scmPathOverride.default_route || HOME_PATH, baseUrl)
          urlObj.searchParams.set('tsg_id', tsg_id)
          const urlOverride = urlObj.toString()
          if (!renameInst.url?.startsWith(scmBaseUrl)) { // ensure go to SCM
            renameInst.url = urlOverride
          }
          const applyLaunchOverrides = (inst: Instance) => {
            try {
              Object.defineProperties(inst, {
                app_launch_display_override: { value: SCM_APP_ID },
                app_launch_url_override: { value: urlOverride },
              })
            }
            catch (_) {
              // ignore
            }
            return inst
          }
          if (renameInst.extra?.hidden && renameInst.app_id === FAWKES_APP_ID &&
            otherInsts.some(inst => [SCM_APP_ID, AIOPS_APP_ID].includes(inst.app_id) && inst.status !== RUNNING)) {
            renameInst.extra.hidden = false // in case hidden prisma_access is running, but the scm or aiops is not
          }
          applyLaunchOverrides(renameInst)
          if (otherInsts.length) {
            for (const inst of otherInsts) {
              const alreadyHidden = inst.extra?.hidden || inst.runtime_attributes?.hidden
              inst.extra = { ...inst.extra, showInTenantManagement: !alreadyHidden }
              if (!inst.extra.hidden) {
                inst.extra.hidden = 'scm-merge' // instead of true to make a difference
              }
              applyLaunchOverrides(inst)
            }
          }
        }
      }
      if (getFrameworkVar('scm_pa_cn_l2') && isPaCnL2(instances.find(inst => inst.app_id === PAE_APP_ID))) {
        flags.is_pa_cn_l2 = true
      }
      // const scmFreeInst = overridden.find(inst => inst.app_id === AIOPS_FREE_APP_ID)
      // if (scmFreeInst && !scmFreeInst.app_launch_display_override) {
      //   Object.defineProperties(scmFreeInst, {
      //     app_launch_display_override: { value: SCM_FREE_APP_ID },
      //   })
      // }
    }

    const scmInst = overridden.find(inst => inst.app_id === SCM_APP_ID)
    if (scmInst) {
      const isEssential = !scmInst.entitlements?.length &&
        !overridden.find(inst => inst.app_id === PAE_APP_ID)?.entitlements?.some((ent: any) =>
          ent?.form_factor_type && ent.app_id === SCM_APP_ID
        )

      Object.defineProperties(scmInst, {
        isPro: { value: !isEssential },
        isEssential: { value: isEssential },
      })
      flags.is_scm_pro = !isEssential
    }

    return overridden
  }
  return instances
}
